import { computed, inject, Injectable, Signal, signal } from '@angular/core'
import { DEV_OR_LOCALHOST } from '@bases/base.token'
import { HttpService } from '@bases/http.service'
import {
  API_URL,
  FORMAT_IMAGE,
  GEOSERVER_URL,
  GEOSERVER_URL_GEOJSON,
} from '@core/constants/constant.data'
import { GEOJSON_FORMAT, HIDDEN_LAYERS_ARRAY, LAYERS_ARRAY } from '@core/constants/layers.data'
import { asyncError, asyncLoading, AsyncState, asyncSuccess } from '@core/models/async-state.model'
import {
  HiddenLayerClass,
  LayerStandard,
  LayerStandardLoading,
  TileLayerOption,
} from '@core/models/layers.model'
import { environment } from '@env/environment'
import * as L from 'leaflet'
import { MatomoTracker } from 'ngx-matomo-client'
import { BehaviorSubject } from 'rxjs'
import { GeojsonService } from './geojson.service'
import { SLDService } from './sld.service'
@Injectable({
  providedIn: 'root',
})
export class LayersService extends HttpService {
  public devOrLocalhost = inject<BehaviorSubject<string>>(DEV_OR_LOCALHOST)
  private readonly _tracker = inject(MatomoTracker)
  private readonly _geoJsonService = inject(GeojsonService)
  private readonly _sldService = inject(SLDService)
  protected _baseUrl = ''

  search$ = signal<string>('')

  layers$ = signal<LayerStandard[]>([])
  layersLoading$ = signal<LayerStandardLoading>({})

  hiddenLayers$ = signal<HiddenLayerClass[]>([])
  availableGeoserverLayerListTitlesState$ = signal<AsyncState<string[]>>(asyncLoading())

  availableLayers$ = computed(() => {
    const titlesState = this.availableGeoserverLayerListTitlesState$()
    if (titlesState.status !== 'success') return []
    // removes layers not found in geoserver
    return this.layers$().filter((layer) => {
      if (
        titlesState.data.some((title) => title.toLowerCase() === layer.workspacename.toLowerCase())
      )
        return layer
    })
  })

  filteredLayers$: Signal<LayerStandard[]> = computed(() => {
    if (!this.availableLayers$()) return []
    return this.availableLayers$().filter((layer) => {
      if (layer.name.toLowerCase().includes(this.search$().toLowerCase())) {
        return layer
      }
    })
  })

  selectedLayers$ = computed(() => {
    return this.layers$()
      .filter((layer) => layer.isSelected)
      .map((layer) => layer.workspacename)
  })

  layersStandard = new L.FeatureGroup()

  constructor() {
    super()
    this.init()
  }

  async init() {
    this._getGeoserverList()
    await this._getLayerServices().then((layers) => {
      this.layers$.set(layers)
    })

    this._getHiddenLayerServices().then((hiddenLayer) => {
      this.hiddenLayers$.set(hiddenLayer)
    })
  }

  private _getLayerServices(): Promise<LayerStandard[]> {
    return Promise.resolve(
      LAYERS_ARRAY.filter(
        (layer) =>
          layer.projects?.includes(environment.appName) ||
          //personal layer has non 0 id + only layers uploaded on same mode (localhost or dev) are shown
          (layer.id != 0 &&
            ((this.devOrLocalhost.getValue() == 'Localhost' && layer.localhost) ||
              (!(this.devOrLocalhost.getValue() == 'Localhost') && !layer.localhost))),
      ),
    )
  }

  private async _getGeoserverList() {
    const url = API_URL + '/layers/geoserver-list'
    const response = await this.GET(url)
    if (!response) {
      this.availableGeoserverLayerListTitlesState$.set(asyncError())
      return
    }
    const titles = response.map((layer: any) => layer.Title).filter(Boolean)
    this.availableGeoserverLayerListTitlesState$.set(asyncSuccess(titles))
  }

  private _getHiddenLayerServices(): Promise<HiddenLayerClass[]> {
    return Promise.resolve(HIDDEN_LAYERS_ARRAY)
  }

  async showOrRemoveLayerWithBoolean(
    layerName: string,
    order: number | undefined,
    boolean: boolean,
  ) {
    const selectedLayer = this.layers$().find((l) => l.workspacename === layerName)

    this.layers$.update((layers) =>
      layers.map((l) => (l.workspacename === layerName ? { ...l, isSelected: boolean } : l)),
    )
    if (selectedLayer) {
      if (boolean) {
        this._tracker.trackEvent('Layer', 'Layer Selected', layerName)
        const reference = await this._addLayerOnMap(selectedLayer, order)
        this.layers$.update((layers) =>
          layers.map((l) =>
            l.workspacename === layerName ? { ...l, leafletReference: reference } : l,
          ),
        )
      } else {
        this.layersStandard.removeLayer(selectedLayer.leafletReference)
        this.layers$.update((layers) =>
          layers.map((l) => (l.workspacename === layerName ? { ...l, leafletReference: null } : l)),
        )
      }
    }
  }

  private async _addLayerOnMap(layer: LayerStandard, order: number | undefined) {
    let newLayer: L.FeatureGroup
    // Add layers in GeoJSON formats
    if (layer.dataType === GEOJSON_FORMAT && layer.isClickable !== false) {
      //@todo, need to change the check for geojson layers
      this._setLayerLoadingStatus(layer.workspacename, true)
      newLayer = await this._addLayerWithActionGeojson(layer)
      this._setLayerLoadingStatus(layer.workspacename, false)
    } else {
      // Add raster layers and non-clickable GeoJSON layers (rendered via WMS)
      const option = {
        layers: environment.prefixWorkspaceName + layer.workspacename,
        styles: layer.layer_type ?? '',
        format: FORMAT_IMAGE,
        transparent: true,
        version: '1.3.0',
        srs: 'EPSG:4326',
        zIndex: order,
      }

      newLayer = this._addLayerTiles(option, layer.workspacename) as L.FeatureGroup
    }

    // if fetching style failed, new layer is not defined
    if (newLayer) {
      this.layersStandard.addLayer(newLayer) // seems to trigger before newLayer is resolved
      return newLayer
    }
  }

  private _addLayerTiles(option: TileLayerOption, action: string): any {
    const wms_request = L.tileLayer.wms(GEOSERVER_URL, option)
    wms_request.on('load', () => {
      this._setLayerLoadingStatus(action, false)
    })
    wms_request.on('tileunload', () => {})
    wms_request.on('tileloadstart', () => {
      this._setLayerLoadingStatus(action, true)
    })
    wms_request.on('tileerror', (error) => {
      console.error('getTileLayer', error)
      this._setLayerLoadingStatus(action, false)
      // Not tested, dont know how to simulate this
      this._toasterService.openSnackBarError('Error loading tiles for ' + action)
    })
    wms_request.on('loading', () => {})
    return wms_request
  }

  private async _addLayerWithActionGeojson(layer: LayerStandard) {
    const geoJSONResponse = await this.getGeoJSONLayer(layer.workspacename)
    const xmlSld = await this._sldService.getStyleFromGeoserver(layer.workspacename)
    if (geoJSONResponse) {
      if (xmlSld === null) {
        return this._geoJsonService.buildGeoJSONLayerGroup(geoJSONResponse)
      } else {
        return await this._geoJsonService.buildStylizedGeoJsonLayerGroup(geoJSONResponse, xmlSld)
      }
    }
  }

  async getGeoJSONLayer(layerWorkspaceName: String) {
    const url =
      GEOSERVER_URL_GEOJSON +
      `?service=WFS&version=1.0.0&request=GetFeature&typeName=${environment.prefixWorkspaceName}${layerWorkspaceName}&outputFormat=application/json`

    return new Promise((resolve) => {
      this._http.get(url).subscribe({
        next: (sldXml) => {
          return resolve(sldXml)
        },
        error: (e) => {
          console.error(e)
          // To override 404 message of geoserver
          this._toasterService.openSnackBarError(`Error fetching layer: ${layerWorkspaceName}`)
          resolve(null)
        },
      })
    })
  }

  private _setLayerLoadingStatus(layerWorkspaceName: string, isLoading: boolean) {
    this.layersLoading$.update((layers) => ({
      ...layers,
      [layerWorkspaceName]: isLoading,
    }))
  }

  removeAllLayers() {
    this.layersLoading$.set({})
    this.layersStandard.clearLayers()
    this.layers$.update((layers) =>
      layers.map((layer) => ({ ...layer, leafletReference: null, isSelected: false })),
    )
  }
}
